import { useState, type ReactNode } from 'react'
import { Dialog as DialogPrimitive } from 'radix-ui'
import { Link } from 'react-router'
import { Check, X, type LucideIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { IconButton } from './button'

export const Sheet = DialogPrimitive.Root
export const SheetTrigger = DialogPrimitive.Trigger
export const SheetClose = DialogPrimitive.Close

/** Bottom sheet (mobile pattern for menus, pickers and short forms); centered card on large screens. */
export function SheetContent({ title, description, children, className, hideTitle }: { title: string; description?: string; children: ReactNode; className?: string; hideTitle?: boolean }) {
  const { t } = useTranslation()
  // Same focus-return rule as DialogContent (sheets opened from state have no trigger).
  const [opener] = useState<HTMLElement | null>(() => (typeof document === 'undefined' ? null : (document.activeElement as HTMLElement | null)))
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className="sheet-overlay fixed inset-0 z-40 bg-overlay" />
      <DialogPrimitive.Content
        onCloseAutoFocus={(event) => {
          if (opener && opener !== document.body && opener.isConnected) {
            event.preventDefault()
            opener.focus()
          }
        }}
        className={cn(
          'sheet-content fixed inset-x-0 bottom-0 z-50 mx-auto max-h-[90dvh] w-full max-w-lg overflow-y-auto overscroll-contain rounded-t-dialog bg-surface-raised px-4 pb-[max(16px,env(safe-area-inset-bottom))] shadow-float sm:bottom-auto sm:top-1/2 sm:w-[calc(100vw-32px)] sm:max-w-md sm:-translate-y-1/2 sm:rounded-dialog sm:pb-6',
          className,
        )}
      >
        <div aria-hidden className="mx-auto mb-1 mt-2 h-1.5 w-10 rounded-full bg-border-subtle sm:hidden" />
        <div className={cn('flex min-h-12 items-center gap-2 pr-10', hideTitle && 'sr-only')}>
          <DialogPrimitive.Title className="text-title font-semibold">{title}</DialogPrimitive.Title>
        </div>
        <DialogPrimitive.Description className={description ? 'mb-3 text-small text-fg-muted' : 'sr-only'}>
          {description ?? title}
        </DialogPrimitive.Description>
        <div className="pb-2">{children}</div>
        <DialogPrimitive.Close asChild>
          <IconButton aria-label={t('common.close')} className="absolute right-2 top-3 sm:top-2">
            <X aria-hidden className="size-5" />
          </IconButton>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  )
}

export interface SheetAction {
  label: string
  icon?: LucideIcon
  to?: string
  onSelect?: () => void
  tone?: 'default' | 'danger'
  /** Marks the current choice (switchers, sort). */
  checked?: boolean
  description?: string
  hidden?: boolean
}

/**
 * Action sheet: the mobile replacement for dropdown menus and rows of secondary buttons.
 * `trigger` is any element (usually an IconButton); actions close the sheet when chosen.
 */
export function ActionSheet({ title, description, trigger, actions, open, onOpenChange, header }: { title: string; description?: string; trigger?: ReactNode; actions: SheetAction[]; open?: boolean; onOpenChange?: (open: boolean) => void; header?: ReactNode }) {
  const [inner, setInner] = useState(false)
  const isOpen = open ?? inner
  const setOpen = onOpenChange ?? setInner
  return (
    <Sheet open={isOpen} onOpenChange={setOpen}>
      {trigger ? <SheetTrigger asChild>{trigger}</SheetTrigger> : null}
      {isOpen ? (
        <SheetContent title={title} {...(description ? { description } : {})}>
          {header}
          <ul className="divide-y divide-border-subtle overflow-hidden rounded-card bg-surface">
            {actions.filter((a) => !a.hidden).map((a) => {
              const Icon = a.icon
              const body = (
                <>
                  {Icon ? <Icon aria-hidden className={cn('size-5 shrink-0', a.tone === 'danger' ? 'text-danger' : 'text-fg-muted')} /> : null}
                  <span className="min-w-0 flex-1">
                    <span className="block break-words">{a.label}</span>
                    {a.description ? <span className="block text-small text-fg-muted">{a.description}</span> : null}
                  </span>
                  {a.checked ? <Check aria-hidden className="size-5 shrink-0 text-action" /> : null}
                </>
              )
              const cls = cn(
                'flex min-h-14 w-full items-center gap-3 px-4 py-3 text-left text-body no-underline transition-colors duration-fast active:bg-border-subtle/60',
                a.tone === 'danger' ? 'text-danger' : 'text-fg',
              )
              return (
                <li key={a.label}>
                  {a.to ? (
                    <Link to={a.to} className={cls} aria-current={a.checked ? 'true' : undefined} onClick={() => setOpen(false)}>{body}</Link>
                  ) : (
                    <button type="button" className={cls} aria-pressed={a.checked === undefined ? undefined : a.checked} onClick={() => { setOpen(false); a.onSelect?.() }}>{body}</button>
                  )}
                </li>
              )
            })}
          </ul>
        </SheetContent>
      ) : null}
    </Sheet>
  )
}
